import { ArrowLeft, ArrowRight, Star } from 'lucide-react'
import Link from 'next/link'
import * as React from 'react'
import { PiGitFork } from 'react-icons/pi'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'

import { feedbacks, FeedbackType } from '@/data/feedback'

import { PageHeader, PageHeaderDescription, PageHeaderHeading } from '@/components/common/page-header'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Carousel, CarouselApi, CarouselContent, CarouselItem, useCarousel } from '@/components/ui/carousel'
import { Separator } from '@/components/ui/separator'

import { Button } from '../ui/button'

function InformationUser({ keyInfo, info }: { keyInfo: string; info: string }) {
  return (
    <div className="flex justify-between text-slate-700 w-full">
      <p className="text-base">{keyInfo}</p>
      <p className="text-base">{info}</p>
    </div>
  )
}

function FeedbackCard({ feedback }: { feedback: FeedbackType }) {
  return (
    <Card className="flex flex-1 flex-col h-full justify-between rounded-none border-none">
      <div className="mb-6">
        <CardHeader>
          <div className="flex items-center space-x-3">
            <Avatar className="h-14 w-14">
              <AvatarImage src={feedback.author.avatar} alt={feedback.author.username} className="object-cover" />
              <AvatarFallback>{feedback.author.username}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col items-start justify-center space-y-1">
              <p className="text-center text-base font-medium">{feedback.author.username}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-base text-accent-foreground/80">{feedback.message}</p>
        </CardContent>
      </div>
      <CardFooter className="flex flex-col">
        <div className="py-8 w-full">
          <Separator />
        </div>
        <div className="space-y-4 w-full">
          <InformationUser keyInfo="🤖 Kinh nghiệm" info={feedback.author.experience} />
          <InformationUser keyInfo="🏢 Công ty" info={feedback.author.company} />
          <InformationUser keyInfo="💻 Vị trí" info={feedback.author.position} />
          <InformationUser keyInfo="🎓 Học viên" info={feedback.author.course} />
        </div>
      </CardFooter>
    </Card>
  )
}

function CardFeedbackStudent({ feedback }: { feedback: FeedbackType }) {
  return (
    <div className="flex h-full flex-col-reverse lg:flex-row rounded border overflow-hidden">
      <FeedbackCard feedback={feedback} />
      <Card
        className={`flex-[2] aspect-square lg:aspect-auto relative overflow-hidden justify-end flex flex-col border-none shadow-none rounded-none ${feedback.repository.image} bg-center bg-cover bg-no-repeat`}
      >
        <Link href={feedback.repository.href} target="_blank" className="w-full h-full flex flex-col justify-end">
          <div className="absolute h-full w-full from-black/50 bg-gradient-to-t top-0 left-0"></div>
          <CardContent className="p-6 h-1/3 z-20 transition-all text-white flex flex-col justify-end sm:space-y-3 space-y-1">
            <div className="flex flex-col w-full justify-between items-end">
              <div className="w-full text-start xl:text-end">
                <h3 className="sm:text-3xl font-semibold text-xl">{feedback.repository.name}</h3>
              </div>
              <div className="w-full 2xl:flex-1 flex space-x-2 justify-start xl:justify-end">
                <p className="flex text-lg font-medium space-x-2 items-center">
                  <Star className="text-white size-5" />
                  <span>{feedback.repository.start} stars</span>
                </p>
                <p className="flex text-lg font-medium space-x-2 items-center">
                  <PiGitFork className="text-white size-5" />
                  <span>{feedback.repository.fork} forks</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Link>
      </Card>
    </div>
  )
}

function CarouselPagination({ api, activeIndex }: { api: CarouselApi | undefined; activeIndex: number }) {
  const { scrollNext, canScrollNext, canScrollPrev, scrollPrev } = useCarousel()
  return (
    <div className="mt-8 lg:mt-12 flex justify-center items-center gap-4">
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'size-10 rounded-full border bg-white/80 backdrop-blur-sm transition-all duration-300',
          canScrollPrev ? 'opacity-100 hover:bg-white hover:scale-105' : 'opacity-50 cursor-not-allowed'
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
      >
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <div className="flex gap-3 items-center">
        {feedbacks.map((_, index) => (
          <div
            key={index}
            className={cn(
              'transition-all ease-out duration-300 flex items-center justify-center hover:bg-primary cursor-pointer',
              activeIndex === index
                ? 'size-6 bg-primary rounded-full'
                : 'size-4 bg-primary/20 rounded-full hover:bg-primary/40'
            )}
            onClick={() => api?.scrollTo(index)}
          >
            <span className="size-3 rounded-full bg-white" />
          </div>
        ))}
      </div>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'size-10 rounded-full border bg-white/80 backdrop-blur-sm transition-all duration-300',
          canScrollNext ? 'opacity-100 hover:bg-white hover:scale-105' : 'opacity-50 cursor-not-allowed'
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
      >
        <ArrowRight className="h-5 w-5" />
      </Button>
    </div>
  )
}

function ProjectFeedbackContainer() {
  const [api, setApi] = React.useState<CarouselApi>()
  const [current, setCurrent] = React.useState(0)

  React.useEffect(() => {
    if (!api) return

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap())
    })
  }, [api])
  return (
    <div className="container">
      <div className="sm:px-12">
        <Carousel
          className="w-full relative"
          opts={{
            loop: true,
            align: 'center',
            skipSnaps: false,
          }}
          setApi={setApi}
        >
          <CarouselContent>
            {feedbacks.map((fb, index) => (
              <CarouselItem key={index} className="basis-full">
                <CardFeedbackStudent feedback={fb} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPagination api={api} activeIndex={current} />
        </Carousel>
      </div>
    </div>
  )
}

function ProjectFeedbackSectionSectionHeading() {
  const { text } = useLocale()
  return (
    <div className="mt-8 mb-8 md:pb-0">
      <PageHeader className="text-center mx-auto space-y-4">
        <PageHeaderHeading size="default">{text.MAIN.BODY.HEADING_6}</PageHeaderHeading>
        <PageHeaderDescription
          size="default"
          className="max-w-[52.875rem] mx-auto"
          dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(text.MAIN.BODY.HEADING_5) }}
        />
      </PageHeader>
    </div>
  )
}

function ProjectFeedbackSection({ className }: { className?: string }) {
  return (
    <section className={cn('mt-16 sm:mt-20', className)} id="project-feedback">
      <ProjectFeedbackSectionSectionHeading />
      <ProjectFeedbackContainer />
    </section>
  )
}

export default ProjectFeedbackSection
